import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, LogOut, Loader2, Download, ChevronDown, ChevronUp, FileText, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, fmtErr, downloadPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PortalDashboard() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [visits, setVisits] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [downloading, setDownloading] = useState(null);
    const token = localStorage.getItem("portal_token");

    useEffect(() => {
        if (!token) { navigate("/portal"); return; }
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get(`${API}/portal/me`, { headers }),
            axios.get(`${API}/portal/visits`, { headers }),
        ]).then(([m, v]) => { setMe(m.data); setVisits(v.data); })
          .catch(() => { localStorage.removeItem("portal_token"); navigate("/portal"); });
    }, []);

    const logout = () => { localStorage.removeItem("portal_token"); navigate("/portal"); };

    const download = async (visit) => {
        setDownloading(visit.id);
        try {
            await downloadPdf(`/portal/visits/${visit.id}/pdf`, `Prescription_${visit.date?.slice(0, 10)}.pdf`, token);
            toast.success("Prescription downloaded");
        } catch (e) {
            toast.error(fmtErr(e, "Download failed"));
        } finally {
            setDownloading(null);
        }
    };

    if (!me || !visits) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" data-testid="portal-loading" /></div>;

    const { patient, doctor } = me;

    return (
        <div className="min-h-screen bg-[#F8FAF9]">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
                <div className="max-w-4xl mx-auto px-4 py-3.5 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E06D1F] text-white"><HeartPulse className="h-5 w-5" /></span>
                        <div>
                            <p className="font-heading font-bold text-slate-900 leading-tight">{doctor?.clinic_name || "My Clinic"}</p>
                            <p className="text-[11px] text-slate-500 leading-tight">Patient Portal</p>
                        </div>
                    </div>
                    <Button data-testid="portal-logout-btn" variant="outline" size="sm" onClick={logout} className="gap-1.5 text-slate-600"><LogOut className="h-3.5 w-3.5" /> Exit</Button>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-6 space-y-5 animate-fade-up" data-testid="portal-dashboard">
                <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="portal-patient-card">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Welcome back</p>
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{patient.name}</h1>
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                                <span className="font-mono2 text-xs text-[#C2410C] bg-[#FFF4EC] border border-[#F6C9A4] rounded-full px-2 py-0.5">{patient.patient_uid}</span>
                                <span>{patient.age ? `${patient.age} yrs` : ""}{patient.gender ? ` · ${patient.gender}` : ""}</span>
                                <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{patient.phone}</span>
                            </div>
                        </div>
                        <div className="text-right text-sm">
                            <p className="font-semibold text-slate-900">{doctor?.name?.startsWith("Dr") ? doctor.name : `Dr. ${doctor?.name}`}</p>
                            <p className="text-xs text-slate-500">{[doctor?.qualification, doctor?.specialization].filter(Boolean).join(", ")}</p>
                            {doctor?.clinic_phone && <p className="text-xs text-slate-500 mt-0.5">Clinic: {doctor.clinic_phone}</p>}
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">My Consultations <span className="text-sm font-normal text-slate-400">({visits.length})</span></h2>
                    {visits.length === 0 && (
                        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                            <FileText className="h-8 w-8 text-slate-300 mx-auto" />
                            <p className="mt-3 text-sm text-slate-500" data-testid="portal-no-visits">No consultations recorded yet.</p>
                        </div>
                    )}
                    <div className="space-y-3">
                        {visits.map((v) => (
                            <div key={v.id} data-testid={`portal-visit-card-${v.visit_no}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                                <button
                                    data-testid={`portal-visit-toggle-${v.visit_no}`}
                                    onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 text-left transition-colors duration-100"
                                >
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">{v.date?.slice(0, 10)} · Visit #{v.visit_no}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">{v.diagnosis || v.symptoms || "General consultation"} {v.doctor_name ? `· Dr. ${v.doctor_name}` : ""}</p>
                                    </div>
                                    {expanded === v.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                                </button>
                                {expanded === v.id && (
                                    <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-3 animate-fade-up">
                                        {v.symptoms && <div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Complaints</p><p className="text-sm text-slate-700 whitespace-pre-line">{v.symptoms}</p></div>}
                                        {v.diagnosis && <div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Diagnosis</p><p className="text-sm font-medium text-slate-900 whitespace-pre-line">{v.diagnosis}</p></div>}
                                        {v.medicines?.length > 0 && (
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">Medicines</p>
                                                <div className="space-y-1.5">
                                                    {v.medicines.map((m, i) => (
                                                        <div key={i} className="flex flex-wrap items-baseline gap-x-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                                                            <p className="text-sm font-medium text-slate-900">{m.form} {m.name} {m.strength}</p>
                                                            <p className="text-xs text-slate-500">{[m.frequency, m.timing, m.duration].filter(Boolean).join(" · ")}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {v.advice && <div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Advice</p><p className="text-sm text-slate-700 whitespace-pre-line">{v.advice}</p></div>}
                                        {v.follow_up && <p className="text-xs text-slate-500">Follow-up: <b>{v.follow_up}</b></p>}
                                        <Button data-testid={`portal-download-pdf-${v.visit_no}`} size="sm" onClick={() => download(v)} disabled={downloading === v.id} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">
                                            {downloading === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download Prescription
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
